import { CircularProgress, Grid } from '@material-ui/core';
import { jssPreset, StylesProvider } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import AddNewContractModal from 'components/AddNewContractModal';
import Fab from 'components/FabComponent';
import Footer from 'components/Footer';
import SmartBanner from 'components/SmartBanner';
import Toaster from 'components/Toaster';
import { MaintenanceScreen } from 'containers/MaintenancePage';
import NavBarDashboard from 'containers/NavBarDashboard';
import fflipConfig from 'features/config';
import fflip from 'fflip';
import GATracking from 'helpers/GATracking';
import { isReverse } from 'helpers/intl';
import PrivateRoute from 'helpers/routing/PrivateRoute';
import PublicRoute from 'helpers/routing/PublicRoute';
import TransverseRoute from 'helpers/routing/TransverseRoute';
import { fetchMaintenanceFlag } from 'hooks/app/useAppConfig';
import { create } from 'jss';
import rtl from 'jss-rtl';
import _ from 'lodash';
import MuiTheme from 'Material-UI/theme';
import React, { useEffect } from 'react';
import { MobileView } from 'react-device-detect';
import { hotjar } from 'react-hotjar';
import { useDispatch, useSelector } from 'react-redux';
import { RouteProps, Router, Switch } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { ROUTES } from 'routes';
import { history } from 'store';
import { getIsOpenAddNewContractModalSelector } from 'store/contract/selectors';
import LanguageProvider from 'store/LanguageProvider';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import { getUserInfo } from 'store/user/actions';
import styled from 'styled-components';
import ContextStore from 'utils/contextStore';
import ScrollToTop from 'utils/scrollToTop';

const GlobalWrapper = styled.div`
  min-height: 93vh;
`;
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const App: React.FC = (): JSX.Element => {
  const lang = useSelector(getLocaleSelector);
  const isOpen = useSelector(getIsOpenAddNewContractModalSelector);
  const reverse = isReverse(lang);
  const direction = reverse ? 'rtl' : 'ltr';

  const [maintenance, setMaintenance] = React.useState<boolean | null>(null); // null = loading

  useEffect(() => {
    const loadMaintenanceFlag = async () => {
      try {
        const isMaintenance = await fetchMaintenanceFlag();
        setMaintenance(isMaintenance);
      } catch (error) {
        setMaintenance(false); // default to false in case of error
      }
    };
    loadMaintenanceFlag();
  }, []);

  document.dir = direction;
  fflip.config(fflipConfig);

  useEffect(() => {
    GATracking.init();
    GATracking.pageView();
    /* istanbul ignore next */
    if (!_.isEmpty(process.env.REACT_APP_HJID)) {
      const hjid = (process.env.REACT_APP_HJID as unknown) as number;
      const hjsv = (process.env.REACT_APP_HJSV as unknown) as number;
      hotjar.initialize(hjid, hjsv);
    }
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (localStorage.getItem('token'))
      /* istanbul ignore next */
      dispatch(getUserInfo());
    // eslint-disable-next-line
  }, []);

  if (maintenance === null) {
    return (
      <Grid
        container
        justify="center"
        alignItems="center"
        style={{ minHeight: '100vh' }}
      >
        <Grid item>
          <CircularProgress data-testid="loader" />
        </Grid>
      </Grid>
    );
  }

  if (maintenance === true) {
    return <MaintenanceScreen />;
  }

  return (
    <ContextStore>
      <ThemeProvider theme={MuiTheme(direction)}>
        <LanguageProvider>
          <StylesProvider jss={jss}>
            <Router history={history}>
              <MobileView>
                <SmartBanner />
              </MobileView>
              <NavBarDashboard />
              <ScrollToTop />
              <GlobalWrapper>
                <LastLocationProvider>
                  <Switch>
                    {ROUTES.PRIVATE()
                      .filter((ele) => ele.isVisible)
                      .map((props: RouteProps, idx: number) => (
                        <PrivateRoute key={idx} {...props} />
                      ))}
                    {ROUTES.PUBLIC.map((props: RouteProps, idx: number) => (
                      <PublicRoute key={idx} {...props} />
                    ))}
                    {ROUTES.TRANSVERSE.map((props: RouteProps, idx: number) => (
                      <TransverseRoute key={idx} {...props} />
                    ))}
                  </Switch>
                </LastLocationProvider>
                <AddNewContractModal isOpen={isOpen} />
                <Toaster />
                <Fab />
              </GlobalWrapper>
              <Footer />
            </Router>
          </StylesProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ContextStore>
  );
};

export default App;
